/* Lucky Feet a11y core — inert stack + tiered scan bus.
   Loaded before custom.js. Do not observe document.body with subtree. */

window.lfInert = window.lfInert || (function () {
  var hidden = [];
  var SKIP = /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/;

  function skip(el) {
    if (!(el instanceof Element)) return true;
    if (SKIP.test(el.tagName)) return true;
    if (el.id === 'A11yLiveRegionPolite' || el.id === 'A11yLiveRegionAssertive') return true;
    if (el.getAttribute('aria-live')) return true;
    return false;
  }

  function hideTabStops(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach(function (el) {
      if (el.hasAttribute('data-lf-tab-restore')) return;
      if (el.getAttribute('tabindex') === '-1') return;
      el.setAttribute('data-lf-tab-restore', '1');
      el.setAttribute('tabindex', '-1');
    });
  }

  function restoreTabStops(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-lf-tab-restore]').forEach(function (el) {
      el.removeAttribute('tabindex');
      el.removeAttribute('data-lf-tab-restore');
    });
  }

  function hide(el) {
    if (skip(el) || hidden.indexOf(el) !== -1) return;
    if (el.matches && el.matches('a.skip-link')) return;
    el.inert = true;
    el.setAttribute('inert', '');
    /* aria-hidden on a focusable control, or on a box that still contains one,
       fails 4.1.2. inert plus a negative tabindex is the hide. */
    el.removeAttribute('aria-hidden');
    hideTabStops(el);
    hidden.push(el);
  }

  function hideSiblings(fromEl) {
    var node = fromEl;
    while (node && node !== document.body) {
      var parent = node.parentElement;
      if (!parent) break;
      Array.prototype.forEach.call(parent.children, function (child) {
        if (child !== node) hide(child);
      });
      node = parent;
    }
  }

  function release() {
    hidden.forEach(function (el) {
      restoreTabStops(el);
      el.inert = false;
      el.removeAttribute('inert');
      if (el.getAttribute('aria-hidden') === 'true') el.removeAttribute('aria-hidden');
    });
    hidden = [];
  }

  function keepHidden(el) {
    if (!(el instanceof Element) || !el.closest) return true;
    return !!el.closest([
      '.announcement--inactive',
      '.mobile-navigation-drawer',
      'main-search',
      '[hidden]',
      '.disclosure__panel[aria-hidden="true"]',
      '.slideshow__slide[aria-hidden="true"]',
      '.slider__item[aria-hidden="true"]',
      '.product-block-options__item--truncated',
      '[role="option"]',
      'footer details > summary[role="presentation"]',
      '#pagefooter details > summary[role="presentation"]'
    ].join(', '));
  }

  function restoreStrayTab(el) {
    if (keepHidden(el) || el.hasAttribute('data-lf-focus-guard')) return;
    var host = el.parentElement;
    if (host && host.closest('[inert], [aria-hidden="true"]')) return;
    el.removeAttribute('tabindex');
    el.removeAttribute('data-lf-tab-restore');
  }

  /* Reward / Headless UI overlays hide page siblings, then a leftover
     scanner stamps tabindex="-1" on background controls without our
     data-lf-tab-restore flag. Restore header, main, and footer when
     no modal is open. Slow devices often finish that stamp after the
     first close timeout, so this must cover every landmark, not just
     the announcement-bar icons. */
  function releaseChrome() {
    release();
    document.querySelectorAll('header, main, footer, #content, #pagefooter').forEach(function (el) {
      if (!el || (el.closest && el.closest('[role="dialog"][aria-modal="true"]'))) return;
      el.inert = false;
      el.removeAttribute('inert');
      if (el.getAttribute('aria-hidden') === 'true') el.removeAttribute('aria-hidden');
    });
    document.querySelectorAll('header [aria-hidden="true"], main [aria-hidden="true"], footer [aria-hidden="true"]').forEach(function (el) {
      if (keepHidden(el)) return;
      if (el.matches('img, svg, .icon, [role="presentation"]')) return;
      if (!el.querySelector('a[href], button, input, select, textarea')) return;
      el.inert = false;
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    });
    document.querySelectorAll('[data-lf-tab-restore]').forEach(restoreStrayTab);
    document.querySelectorAll([
      'header a[href][tabindex="-1"]',
      'header button[tabindex="-1"]',
      'main a[href][tabindex="-1"]',
      'main button[tabindex="-1"]',
      'footer a[href][tabindex="-1"]',
      'footer button[tabindex="-1"]',
      '#content a[href][tabindex="-1"]',
      '#content button[tabindex="-1"]',
      '#pagefooter a[href][tabindex="-1"]',
      '#pagefooter button[tabindex="-1"]',
      '.skip-link[tabindex="-1"]',
      '.announcement-bar .social__link[tabindex="-1"]'
    ].join(', ')).forEach(restoreStrayTab);
    document.querySelectorAll('.announcement--inactive').forEach(function (slide) {
      hideTabStops(slide);
    });
  }

  return {
    hide: hide,
    hideSiblings: hideSiblings,
    release: release,
    releaseChrome: releaseChrome,
    hideTabStops: hideTabStops,
    restoreTabStops: restoreTabStops
  };
})();

/* Shared a11y scan bus.
   Watch known late-injection roots (Klaviyo, search, account, country, overlays)
   plus body *direct* children. Do not observe document.body with subtree —
   that stalled the storefront. Closed-shadow / cross-origin vendors stay unwatched.
   Body class changes and overlay clicks run critical tasks only. */
window.lfA11y = window.lfA11y || (function () {
  var critical = [];
  var idle = [];
  var timer = 0;
  var idleTimer = 0;
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;
  var WATCH_SEL = [
    'main-search',
    'shopify-account',
    'country-selector',
    'cart-form',
    'cart-drawer',
    'announcement-bar',
    'scrolling-banner',
    '.localization',
    '.js-cart-drawer',
    '.instafeed-lightbox',
    '.instafeed-modal-wrapper',
    '.instafeed-modals-wrapper',
    '.klaviyo-form',
    '[data-testid^="klaviyo-form"]',
    '.zn-container',
    '.jdgm-widget',
    '.jdgm-rev-widg',
    '.jdgm-form',
    '[class*="ai-foot-selector"]',
    '[class*="ai-foot-solutions"]',
    '[class*="ai-consultation"]',
    '#headlessui-portal-root',
    '.lp99_section-wrapper',
    '#ways-to-earn-section',
    '#hero-section-section',
    '.lp99_button-container',
    '#shopify-pc__banner',
    '#shopify-pc__prefs',
    '#faq-section',
    '[class*="referral-faq"]'
  ].join(', ');

  var stats = /lfperf/.test(location.search) ? (window.__lfA11yStats = []) : null;
  function runOne(fn) {
    var t0 = stats ? performance.now() : 0;
    try { fn(); } catch (err) {}
    if (stats) {
      var ms = performance.now() - t0;
      if (ms > 4) stats.push({ ms: Math.round(ms), src: String(fn).slice(0, 140) });
    }
  }

  /* Every observer the bus owns. After our own tasks run we drop the
     records they produced, so a task that rewrites text inside a watched
     root cannot re-trigger the scan (that loop ran scans nonstop on
     collection pages). Other scripts mutate in their own tasks, so their
     records are never dropped here. */
  var observers = [];
  function flush() {
    for (var i = 0; i < observers.length; i++) observers[i].takeRecords();
  }

  function runList(list) {
    for (var i = 0; i < list.length; i++) runOne(list[i]);
    flush();
  }

  /* Time-sliced runner: yields to the main thread every ~30ms so a full
     scan never becomes one long task (keeps INP/TBT low). A request that
     arrives while a pass is running marks it dirty and reruns once. */
  var BUDGET = 30;
  var busy = { critical: false, idle: false };
  var dirty = { critical: false, idle: false };
  function yieldNow() {
    if (window.scheduler && typeof window.scheduler.yield === 'function') return window.scheduler.yield();
    return new Promise(function (r) { window.setTimeout(r, 0); });
  }
  function runSliced(list, key) {
    if (busy[key]) { dirty[key] = true; return; }
    busy[key] = true;
    dirty[key] = false;
    var i = 0;
    var step = function () {
      var start = performance.now();
      while (i < list.length) {
        runOne(list[i++]);
        if (i < list.length && performance.now() - start > BUDGET) {
          flush();
          yieldNow().then(step);
          return;
        }
      }
      flush();
      busy[key] = false;
      if (dirty[key]) runSliced(list, key);
    };
    step();
  }

  function schedule() {
    if (timer) return;
    timer = window.setTimeout(function () {
      timer = 0;
      runSliced(critical, 'critical');
    }, 80);
  }

  function scheduleIdle() {
    if (idleTimer) return;
    var run = function () {
      idleTimer = 0;
      runSliced(idle, 'idle');
    };
    if (typeof window.requestIdleCallback === 'function') {
      idleTimer = window.requestIdleCallback(run, { timeout: 1000 });
    } else {
      idleTimer = window.setTimeout(run, 1);
    }
  }

  function once(fn, opts) {
    if (typeof fn !== 'function') return;
    var priority = opts && opts.priority === 'critical' ? 'critical' : 'idle';
    if (priority === 'critical') {
      critical.push(fn);
      runOne(fn);
      flush();
      return;
    }
    idle.push(fn);
    scheduleIdle();
  }

  function scan(priority) {
    if (priority === 'idle') {
      scheduleIdle();
      return;
    }
    if (priority === 'all') {
      runList(critical);
      runList(idle);
      return;
    }
    schedule();
  }

  function watchRoot(el) {
    if (!el || (watched && watched.has(el))) return;
    if (watched) watched.add(el);
    var mo = new MutationObserver(scheduleIdle);
    mo.observe(el, { childList: true, subtree: true });
    observers.push(mo);
  }

  function watchKnown() {
    document.querySelectorAll(WATCH_SEL).forEach(watchRoot);
  }

  function isLateWidget(el) {
    if (!(el instanceof Element)) return false;
    var hint = (el.id || '') + ' ' + (el.className || '') + ' ' + el.tagName;
    return /klaviyo|instafeed|kl-hub|shopify-account|country-selector|99minds|lp99|shopify-pc/i.test(hint);
  }

  var bound = false;
  function bindTargets() {
    if (bound) return;
    bound = true;
    document.addEventListener('on:cart:after-merge', scheduleIdle);
    document.addEventListener('on:quickbuy:after-open', schedule);
    document.addEventListener('on:cart-drawer:after-open', schedule);
    document.addEventListener('on:carousel-slider:select', scheduleIdle);
    if (!document.body) return;
    var bodyMo = new MutationObserver(function (mutations) {
      var classChanged = false;
      var nodesAdded = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'attributes') {
          classChanged = true;
          continue;
        }
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          if (nodes[j].nodeType !== 1) continue;
          nodesAdded = true;
          if (isLateWidget(nodes[j])) watchRoot(nodes[j]);
        }
      }
      if (classChanged) schedule();
      if (nodesAdded) {
        watchKnown();
        scheduleIdle();
      }
    });
    bodyMo.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true
    });
    observers.push(bodyMo);
    watchKnown();
    document.querySelectorAll('[data-ajax-container], cart-form').forEach(watchRoot);
    document.addEventListener('click', function (event) {
      var t = event.target.closest && event.target.closest(
        '.show-search-link, shopify-account, .header-account-link, country-selector, .localization, [class*="instafeed"], .klaviyo-close-form, .navigation__item--with-children, .page-btn, [name="next"], [name="prev"], .main-search__button, [id^="shopify-pc__"]'
      );
      if (!t) return;
      scan('critical');
    }, true);
    window.addEventListener('load', function () {
      scheduleIdle();
    });
  }

  function isCardChrome(el) {
    if (!(el instanceof Element) || !el.closest) return false;
    if (/swatch/i.test(String(el.className || ''))) return true;
    return !!el.closest([
      'product-block',
      '.product-block',
      '.product-block__detail',
      '.product-block-options',
      'variant-king-combined-listing',
      '.swatch-navigable-wrapper',
      '.swatch-navigable',
      '.swatch-view',
      '.swatches'
    ].join(', '));
  }

  if (document.body) {
    bindTargets();
  } else {
    document.addEventListener('DOMContentLoaded', bindTargets);
  }
  return { once: once, scan: scan, isCardChrome: isCardChrome };
})();
